import api from "@/lib/api";
import { assertApiBodyOk } from "@/utils/apiBusinessError";
import { getCookie } from "cookies-next";
import {
  readFileAsBase64Payload,
} from "@/app/dashboard/facility-resident-risk-event-document/utils/fileUpload";
import type {
  FeatureDocument,
  FeatureDocumentFormValues,
  FeatureDocumentUploadResult,
} from "../types";

function strParam(value: unknown): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function authStringParams(): {
  p_company_id: string;
  p_logged_in_company_user_id: string;
} {
  return {
    p_company_id: strParam(getCookie("company_id")),
    p_logged_in_company_user_id: strParam(getCookie("company_user_id")),
  };
}

function modifiedByParam(): string {
  return strParam(getCookie("company_user_id"));
}

function normalizeActiveFlagString(
  flag: boolean | string | number | null | undefined,
): string {
  if (flag === false || flag === 0 || flag === "false" || flag === "0") {
    return "false";
  }
  return "true";
}

/** Swagger POST /v1/feature_document/new — auth via Token header only, not p_auth_token. */
type FeatureDocumentCreateBody = {
  p_company_id: string;
  p_logged_in_company_user_id: string;
  p_document_name: string;
  p_document_type: string;
  p_s3_document_location: string;
  p_feature: string;
  p_feature_menu_url: string;
  p_modified_by: string;
};

/** Swagger PUT /v1/feature_document/update */
type FeatureDocumentUpdateBody = FeatureDocumentCreateBody & {
  p_feature_document_id: string;
  p_active_flag: string;
};

function buildCreatePayload(values: FeatureDocumentFormValues): FeatureDocumentCreateBody {
  return {
    p_company_id: strParam(getCookie("company_id")),
    p_logged_in_company_user_id: strParam(getCookie("company_user_id")),
    p_document_name: values.document_name.trim(),
    p_document_type: strParam(values.document_type),
    p_s3_document_location: strParam(values.s3_document_location),
    p_feature: strParam(values.feature),
    p_feature_menu_url: strParam(values.feature_menu_url),
    p_modified_by: modifiedByParam(),
  };
}

function buildUpdatePayload(
  featureDocumentId: number,
  values: FeatureDocumentFormValues,
  activeFlag: boolean | string | number | null | undefined,
): FeatureDocumentUpdateBody {
  return {
    ...buildCreatePayload(values),
    p_feature_document_id: strParam(featureDocumentId),
    p_active_flag: normalizeActiveFlagString(activeFlag),
  };
}

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === "object") return [data as T];
  return [];
}

function firstRecord(data: unknown): Record<string, unknown> | null {
  const rows = asArray<Record<string, unknown>>(data);
  return rows[0] ?? null;
}

export function mapApiRowToFeatureDocument(raw: Record<string, unknown>): FeatureDocument {
  const id = Number(
    raw.feature_document_id ??
      raw.p_feature_document_id ??
      raw.id ??
      raw.p_id,
  );

  return {
    feature_document_id: Number.isNaN(id) ? 0 : id,
    document_name: String(raw.document_name ?? raw.p_document_name ?? ""),
    document_type: String(raw.document_type ?? raw.p_document_type ?? ""),
    s3_document_location: String(
      raw.s3_document_location ?? raw.p_s3_document_location ?? "",
    ),
    s3_bucket_path: String(raw.s3_bucket_path ?? raw.p_s3_bucket_path ?? "").trim() || undefined,
    s3_bucket_url: String(raw.s3_bucket_url ?? raw.p_s3_bucket_url ?? "").trim() || undefined,
    feature: String(raw.feature ?? raw.p_feature ?? ""),
    feature_menu_url: String(raw.feature_menu_url ?? raw.p_feature_menu_url ?? ""),
    active_flag:
      raw.active_flag === false ||
      raw.active_flag === "false" ||
      raw.active_flag === 0 ||
      raw.active_flag === "0"
        ? false
        : raw.active_flag != null
          ? true
          : undefined,
    company_id: raw.company_id != null ? Number(raw.company_id) : undefined,
    created_datetime:
      raw.created_datetime != null ? String(raw.created_datetime) : null,
    modified_datetime:
      raw.modified_datetime != null ? String(raw.modified_datetime) : null,
    modified_by_name:
      raw.modified_by_name != null ? String(raw.modified_by_name) : null,
  };
}

export async function fetchFeatureDocuments(): Promise<FeatureDocument[]> {
  const response = await api.get("/v1/feature_document/all", {
    params: authStringParams(),
  });

  assertApiBodyOk(response.data, "Failed to load feature documents");

  return asArray<Record<string, unknown>>(response.data)
    .map(mapApiRowToFeatureDocument)
    .filter((doc) => doc.active_flag !== false);
}

export async function fetchFeatureDocumentById(
  featureDocumentId: number,
): Promise<FeatureDocument | null> {
  const response = await api.get("/v1/feature_document/show", {
    params: {
      p_company_id: strParam(getCookie("company_id")),
      p_logged_in_company_user_id: strParam(getCookie("company_user_id")),
      p_feature_document_id: strParam(featureDocumentId),
    },
  });

  assertApiBodyOk(response.data, "Failed to load feature document");

  const row = firstRecord(response.data);
  return row ? mapApiRowToFeatureDocument(row) : null;
}

export async function uploadFeatureDocumentFile(
  file: File,
): Promise<FeatureDocumentUploadResult & { s3_document_location: string }> {
  const { base64, contentType, extension } = await readFileAsBase64Payload(file);

  const response = await api.post("/v1/feature_document/file_upload", {
    p_company_id: strParam(getCookie("company_id")),
    p_file_content: base64,
    p_file_content_type: contentType,
    p_file_extension: extension,
  });

  assertApiBodyOk(response.data, "Failed to upload document");

  const s3DocumentLocation =
    response.data?.s3_bucket_path ??
    response.data?.s3_bucket_url ??
    response.data?.s3_document_location ??
    "";

  if (!s3DocumentLocation) {
    throw new Error("Upload succeeded but no document location was returned.");
  }

  return {
    s3_bucket_path: response.data?.s3_bucket_path,
    s3_bucket_url: response.data?.s3_bucket_url,
    file_type: response.data?.file_type,
    s3_document_location: String(s3DocumentLocation),
  };
}

export async function createFeatureDocument(
  values: FeatureDocumentFormValues,
): Promise<void> {
  const body: FeatureDocumentCreateBody = buildCreatePayload(values);

  const response = await api.post("/v1/feature_document/new", body);

  assertApiBodyOk(response.data, "Failed to create feature document");
}

export async function updateFeatureDocument(
  featureDocumentId: number,
  values: FeatureDocumentFormValues,
  activeFlag: boolean | string | number | null | undefined = true,
): Promise<void> {
  const body: FeatureDocumentUpdateBody = buildUpdatePayload(
    featureDocumentId,
    values,
    activeFlag,
  );

  const response = await api.put("/v1/feature_document/update", body);

  assertApiBodyOk(response.data, "Failed to update feature document");
}

function documentToFormValues(document: FeatureDocument): FeatureDocumentFormValues {
  return {
    document_name: document.document_name,
    document_type: document.document_type,
    s3_document_location: document.s3_document_location,
    feature: document.feature,
    feature_menu_url: document.feature_menu_url,
    selected_feature_url: document.feature_menu_url,
  };
}

/** Soft-delete via existing PUT /update with p_active_flag = false. */
export async function deleteFeatureDocument(document: FeatureDocument): Promise<void> {
  await updateFeatureDocument(
    document.feature_document_id,
    documentToFormValues(document),
    false,
  );
}
